import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { forkJoin, switchMap, timer } from 'rxjs';
import { AuthenticationService } from '../../auth/authentication.service';
import { CFDocumentToDisplay } from '../../models/cf-document/cf-document-to-display';
import { CFSignatureForComplete } from '../../models/signatures/cf-signature-for-complete';
import { CustomNotificationService } from '../../services/custom-notification.service';
import { DateService } from '../../services/date.service';
import { CFDocumentStoreService } from '../../services/documents/cf-document-store.service';
import { CFDocumentService } from '../../services/documents/cf-document.service';
import { CFSignatureStoreService } from '../../services/signatures/cf-signature-store.service';
import { CFSignatureService } from '../../services/signatures/cf-signature.service';
import { CustomMessagesService } from '../../services/translation/custom-messages.service';

@UntilDestroy()
@Component({
    selector: 'clfl-cf-signature-qr',
    templateUrl: './cf-signature-qr.component.html',
    styleUrls: ['./cf-signature-qr.component.scss']
})
export class CfSignatureQrComponent implements OnInit, OnDestroy {
    @ViewChild('form') form!: NgForm;

    public document!: CFDocumentToDisplay;
    public pinCode: string | undefined;

    public isLoading = false;
    public isDocumentLoaded = false;

    constructor(
        private documentStoreService: CFDocumentStoreService,
        private documentService: CFDocumentService,
        private signatureService: CFSignatureService,
        private signatureStoreService: CFSignatureStoreService,
        private route: ActivatedRoute,
        private router: Router,
        private notificationService: CustomNotificationService,
        private msgService: CustomMessagesService,
        private dateService: DateService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.isDocumentLoaded = false;
        this.authenticationService.isQrSignatureRoute = true;
        this.route.data
            .pipe(
                untilDestroyed(this),
                switchMap((data: { document: CFDocumentToDisplay }) => {
                    if (data.document) {
                        this.document = data.document;
                        this.documentStoreService.setDocumentData(data.document);

                        const blobDocument$ = this.documentService.downloadLatestDocument(
                            this.documentStoreService.selectedDocumentType,
                            this.documentStoreService.companyId,
                            this.document.externalId
                        );

                        return forkJoin([blobDocument$]);
                    }

                    return forkJoin([]);
                })
            )
            .subscribe({
                next: (response) => {
                    if (response && response.length > 0) {
                        this.isDocumentLoaded = true;
                        this.isLoading = false;

                        this.documentStoreService.latestDocumentBlob = response[0];

                        const sectionAboutToSign = this.document.sections.find(
                            (section) => section.signatureId === this.signatureStoreService.signatureId
                        );

                        if (sectionAboutToSign.isSigned) {
                            this.router.navigate(['/thanks-for-signing']);
                        }

                        this.notificationService.success(this.msgService.translate('cf-document.notification.documentFound'));
                    }
                }
            });
    }

    public save(): void {
        this.form.control.markAllAsTouched();
        if (!this.form.valid) {
            return;
        }

        this.applyPinSignature();
    }

    public applyPinSignature(): void {
        this.isLoading = true;
        const signatureForComplete = new CFSignatureForComplete(undefined, this.pinCode.toString());

        this.signatureService
            .completeSignature(this.document.cloudFlowId, this.signatureStoreService.signatureId, signatureForComplete)
            .pipe(
                untilDestroyed(this),
                switchMap(() => {
                    return timer(10000).pipe(
                        switchMap(() =>
                            this.documentService.downloadLatestDocument(
                                this.documentStoreService.selectedDocumentType,
                                this.documentStoreService.companyId,
                                this.document.externalId
                            )
                        )
                    );
                })
            )
            .subscribe({
                next: (response: Blob) => {
                    this.documentStoreService.latestDocumentBlob = response;
                    this.notificationService.success(this.msgService.translate('cf-signature.notification.signatureComplete'));
                    this.isLoading = false;
                    this.router.navigate(['/thanks-for-signing']);
                },
                error: (err) => {
                    this.isLoading = false;
                }
            });
    }

    ngOnDestroy(): void {
        this.authenticationService.logout();
    }
}
